import { useEffect, useState } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import Modal from "../../../components/ui/Modal";
import Skeleton from "../../../components/ui/Skeleton";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import Textarea from "../../../components/ui/Textarea";
import StatusBadge from "../../../components/ui/StatusBadge";
import { EmptyState } from "../../../components/ui/FeedbackStates";
import masterService from "../../../services/masterEntitiesService";
import { toast } from "react-toastify";
import { Building2, Edit2, Plus, Trash2 } from "lucide-react";
import styles from "../Masters.module.css";

export default function MasterPropertyTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadTypes = async () => {
    setLoading(true);
    try {
      const data = await masterService.getPropertyTypes();
      setTypes(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load property types.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const openCreateModal = () => {
    setIsCreate(true);
    setEditingType(null);
    setName("");
    setCode("");
    setCodeManuallyEdited(false);
    setDescription("");
    setModalOpen(true);
  };

  const openEditModal = (t) => {
    setIsCreate(false);
    setEditingType(t);
    setName(t.name);
    setCode(t.code);
    setCodeManuallyEdited(true);
    setDescription(t.description || "");
    setModalOpen(true);
  };

  const handleNameChange = (val) => {
    setName(val);
    if (isCreate && !codeManuallyEdited) {
      const autoCode = String(val || "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      setCode(autoCode);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Property type name is required.");
      return;
    }

    setSubmitting(true);
    try {
      if (isCreate) {
        await masterService.createPropertyType({
          name: name.trim(),
          code: code.trim().toUpperCase() || undefined,
          description: description.trim(),
          is_active: true,
        });
        toast.success("New property type created successfully!");
      } else {
        await masterService.updatePropertyType(editingType.id, {
          name: name.trim(),
          description: description.trim(),
        });
        toast.success("Property type updated successfully!");
      }
      setModalOpen(false);
      loadTypes();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || `Failed to ${isCreate ? "create" : "update"} property type.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Are you sure you want to delete "${t.name}" (${t.code})?`)) return;
    try {
      await masterService.deletePropertyType(t.id);
      toast.success(`Property type "${t.name}" deleted.`);
      loadTypes();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete property type.");
    }
  };

  const toggleStatus = async (t) => {
    try {
      await masterService.updatePropertyType(t.id, { is_active: !t.is_active });
      toast.success(`${t.code} is now ${!t.is_active ? "Active" : "Inactive"}`);
      loadTypes();
    } catch (err) {
      console.error(err);
      toast.error("Failed to toggle status.");
    }
  };

  return (
    <AdminLayout title="Master Property Types">
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              <Building2 size={24} color="#6366f1" />
              Master Property Types
            </h1>
            <p className={styles.subtitle}>
              Global taxonomy of property layouts (Residential, Industrial, Commercial, Villa, Co-Working, Retail, Custom).
            </p>
          </div>
          <Button variant="primary" icon={<Plus size={16} />} onClick={openCreateModal}>
            Add Property Type
          </Button>
        </div>

        {loading ? (
          <div style={{ padding: "24px" }}>
            <Skeleton variant="card" count={3} />
          </div>
        ) : types.length === 0 ? (
          <EmptyState
            title="No property types defined"
            description="The master property taxonomy is currently unpopulated."
            action={
              <Button variant="primary" icon={<Plus size={16} />} onClick={openCreateModal}>
                Add First Property Type
              </Button>
            }
          />
        ) : (
          <div className={styles.grid}>
            {types.map((t) => (
              <div key={t.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <div
                    className={styles.logoWrap}
                    style={{ background: "#ede9fe", color: "#6366f1", fontWeight: 700, fontSize: "14px" }}
                  >
                    {t.code.slice(0, 3)}
                  </div>
                  <div className={styles.cardInfo}>
                    <div className={styles.companyName}>{t.name}</div>
                    <span className={styles.codeBadge}>{t.code}</span>
                  </div>
                </div>

                <p className={styles.descText}>{t.description || "No description provided."}</p>

                <div className={styles.cardBottom} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button
                    type="button"
                    className={styles.statusBadgeBtn}
                    onClick={() => toggleStatus(t)}
                    title={`Click to ${t.is_active ? "deactivate" : "activate"}`}
                  >
                    <StatusBadge
                      status={t.is_active ? "ACTIVE" : "INACTIVE"}
                      label={t.is_active ? "Active" : "Inactive"}
                    />
                  </button>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <Button variant="outline" size="sm" onClick={() => openEditModal(t)}>
                      <Edit2 size={14} style={{ marginRight: "4px" }} /> Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      style={{ color: "var(--color-danger, #ef4444)" }}
                      onClick={() => handleDelete(t)}
                      title={`Delete ${t.name}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={isCreate ? "Add New Property Type" : `Edit Property Type (${editingType?.code})`}
        >
          <form onSubmit={handleSubmit} className={styles.form} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <FormField label="Display Name" required>
              <TextInput
                value={name}
                onChange={(e) => handleNameChange(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                placeholder="e.g. Resort & Hotel, Data Center, Warehousing..."
                required
                autoFocus
              />
            </FormField>

            <FormField
              label="System Code (Unique Identifier)"
              required={isCreate}
              helperText={isCreate ? "Unique uppercase code (e.g. WAREHOUSE_LOGISTICS)" : undefined}
            >
              <TextInput
                value={code}
                onChange={(e) => {
                  setCode(e?.target ? e.target.value.toUpperCase() : (e?.value ?? e ?? "").toUpperCase());
                  setCodeManuallyEdited(true);
                }}
                placeholder="e.g. DATA_CENTER"
                disabled={!isCreate}
                required={isCreate}
              />
            </FormField>

            <FormField label="Description">
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                placeholder="Brief explanation of this property layout archetype and operational structure"
              />
            </FormField>

            <div className={styles.formActions} style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
              <Button variant="outline" onClick={() => setModalOpen(false)} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting} loading={submitting}>
                {isCreate ? "Create Property Type" : "Save Changes"}
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </AdminLayout>
  );
}
